import { useMemo } from "react";
import { Check } from "lucide-react";
import type { Topic } from "@z-suite/shared";

const CATEGORY_LABELS: Record<string, string> = {
  ekonomi: "Ekonomi & Finans",
  sektor: "Sektörler",
  teknoloji: "Teknoloji",
  politika: "Politika & Regülasyon",
  dunya: "Dünya",
  genel: "Genel",
};

const CATEGORY_ORDER = ["ekonomi", "sektor", "teknoloji", "politika", "dunya", "genel"];

export function TopicPicker({
  topics,
  selected,
  onToggle,
}: {
  topics: Topic[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, Topic[]>();
    for (const t of topics) {
      const key = t.category ?? "genel";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return [...map.entries()].sort(
      (a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]),
    );
  }, [topics]);

  return (
    <div className="space-y-8">
      {grouped.map(([category, items]) => (
        <div key={category}>
          <div className="mb-3 flex items-center gap-3">
            <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
              {CATEGORY_LABELS[category] ?? category}
            </h3>
            <div className="rule-gold flex-1" />
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {items.map((topic) => {
              const isSelected = selected.has(topic.id);
              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => onToggle(topic.id)}
                  aria-pressed={isSelected}
                  className={`group relative flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-sm transition-all duration-200 ${
                    isSelected
                      ? "border-gold/60 bg-gold/10 text-white shadow-[0_0_0_1px_rgba(201,162,39,0.3)]"
                      : "border-slate-700/50 bg-ink-800/40 text-slate-300 hover:border-slate-500/70 hover:bg-ink-800/70"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
                      isSelected ? "border-gold bg-gold text-ink-900" : "border-slate-600 text-transparent"
                    }`}
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <span className="leading-tight">{topic.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
